(ns story-builder.story-editor.breakpoint.util
  (:require
   [clojure.string :as str]
   [story-builder.translation :refer [tr]]
   [story-builder.util.core :as util]))

(defn breakpoint-active?
  [{:keys [orientation
           fixed
           min
           max]
    :or   {orientation :any}}
   [w h]]
  (and (or (= orientation :any)
           (and (= orientation :portrait) (> h w))
           (and (= orientation :landscape) (> w h)))
       (or (not fixed)
           (let [{:keys [width
                         height
                         aspect-ratio]} fixed]
             (and (or (not width)
                      (= (:value width) w))
                  (or (not height)
                      (= (:value height) h))
                  (or (not aspect-ratio)
                      (= (.toFixed (apply / aspect-ratio) 2)
                         (.toFixed (/ w h) 2))))))
       (or (not min)
           (let [{:keys [width
                         height
                         aspect-ratio]} min]
             (and (or (not width)
                      (>= w (:value width)))
                  (or (not height)
                      (>= h (:value height)))
                  (or (not aspect-ratio)
                      (>= (/ w h)  (apply / aspect-ratio))))))
       (or (not max)
           (let [{:keys [width
                         height
                         aspect-ratio]} max]
             (and (or (not width)
                      (<= w (:value width)))
                  (or (not height)
                      (<= h (:value height)))
                  (or (not aspect-ratio)
                      (<= (/ w h)  (apply / aspect-ratio))))))))

(def user-repr-key-order [:width :height])

(defn user-repr-sorted-map [m]
  (-> (sorted-map-by (fn [k1 k2]
                       (< (util/index-of user-repr-key-order k1)
                          (util/index-of user-repr-key-order k2))))
      (into m)))

(defn user-repr
  [breakpoint]
  (str/join ", "
            (->> breakpoint
                 (map
                  (fn [[k v]]
                    (case k
                      :orientation
                      (tr [:orientation-x "Orientation: %1"] [(get {:any       (tr [:any "Any"])
                                                                    :portrait  (tr [:portrait "Portrait"])
                                                                    :landscape (tr [:landscape "Landscape"])} v)])
                      :fixed
                      (let [v (user-repr-sorted-map v)]
                        (str/join ", "
                                  (map
                                   (fn [[k v]]
                                     (case k
                                       :width (tr [:w-w "W %1px"] [(:value v)])
                                       :height (tr [:h-h "H %1px"] [(:value v)])
                                       :aspect-ratio (tr [:aspect-ratio-x "Aspect-ratio: %1"] [(str/join "/" v)])))
                                   v)))
                      :min
                      (str/join ", "
                                (map
                                 (fn [[k v]]
                                   (case k
                                     :width (tr [:min-w-w "Min-W %1px"] [(:value v)])
                                     :height (tr [:min-h-h "Min-H %1px"] [(:value v)])
                                     :aspect-ratio (tr [:min-aspect-ratio-x "Min aspect-ratio: %1"] [(str/join "/" v)])))
                                 v))
                      :max
                      (str/join ", "
                                (map
                                 (fn [[k v]]
                                   (case k
                                     :width (tr [:max-w-w "Max-W %1px"] [(:value v)])
                                     :height (tr [:max-h-h "Max-H %1px"] [(:value v)])
                                     :aspect-ratio (tr [:max-aspect-ratio-x "Max aspect-ratio: %1"] [(str/join "/" v)])))
                                 v)))))
                 (remove str/blank?))))

(defn fixed-sizes [breakpoints]
  (reduce (fn [res breakpoint]
            (if-let [fixed (:fixed breakpoint)]
              (let [{:keys [width height]} fixed]
                (if (and width height)
                  (conj res [(:value width) (:value height)])
                  res))
              res))
          []
          breakpoints))

(defn fixed-size [breakpoint]
  (when-let [fixed (:fixed breakpoint)]
    (let [width  (-> fixed :width :value)
          height (-> fixed :height :value)]
      [width height])))

(defn get-prop
  [layer active-breakpoints prop]
  (if (seq active-breakpoints)
    (get-in layer [:breakpoint (last active-breakpoints) prop] (get layer prop))
    (get layer prop)))

(defn set-prop
  [layer breakpoint prop value]
  (if breakpoint
    (assoc-in layer [:breakpoint breakpoint prop] value)
    (assoc layer prop value)))

(defn dissoc-prop
  [layer breakpoint prop]
  (cond
    (keyword? prop)
    (if breakpoint
      (update-in layer [:breakpoint breakpoint] dissoc prop)
      (dissoc layer prop))

    (vector? prop)
    (let [[prop & path] (reverse prop)]
      (if breakpoint
        (update-in layer (into [:breakpoint breakpoint] path) dissoc prop)
        (update-in layer path dissoc prop)))))

(defn get-path
  [layer active-breakpoints path]
  (if (seq active-breakpoints)
    (get-in layer (into [:breakpoint (last active-breakpoints)] path) (get-in layer path))
    (get-in layer path)))

(defn update-path
  [layer {:keys [breakpoint path]} f]
  (if breakpoint
    (update-in layer (into [:breakpoint breakpoint] path) f)
    (update-in layer path f)))

(defn set-path
  [layer breakpoint path value]
  (if breakpoint
    (assoc-in layer (into [:breakpoint breakpoint] path) value)
    (assoc-in layer path value)))

(defn get-props
  ([layer active-breakpoints]
   (get-props layer active-breakpoints :style))
  ([layer active-breakpoints key]
   (if (seq active-breakpoints)
     (apply util/deep-merge
            (into [(if key
                     (get-in layer (if (vector? key) key [key]))
                     layer)]
                  (map #(get-in layer (cond
                                        (vector? key) (into [:breakpoint %] key)
                                        key [:breakpoint % key]
                                        :else [:breakpoint %]))
                       active-breakpoints)))
     (if key
       (get-in layer (if (vector? key) key [key]))
       layer))))

(defn merge-styles
  [layer breakpoint style]
  (update-in layer (if breakpoint [:breakpoint breakpoint :style] [:style]) util/deep-merge style))

(defn update-styles
  [layer {:keys [breakpoint
                 key]
          :or   {key :style}} f]
  (if breakpoint
    (update-in layer [:breakpoint breakpoint key] f)
    (update layer key f)))

(defn most-specific-breakpoint [breakpoints]
  (or (first (filter :fixed breakpoints))
      (first breakpoints)))

(defn specificity [breakpoint]
  (if (:orientation breakpoint)
    0
    1))

(defn sort-by-desc-specificity [breakpoints]
  (->> breakpoints
       (sort-by specificity)
       (vec)))

(defn selected-breakpoint
  [active-breakpoints current-selected]
  (if (contains? (set active-breakpoints) current-selected)
    current-selected
    (most-specific-breakpoint active-breakpoints)))

(defn remove-breakpoint
  [story-config breakpoint]
  (-> story-config
      (update :breakpoints  (fn [breakpoints] (filterv #(not= % breakpoint) breakpoints)))
      (update :scenes
              (fn [scenes]
                (mapv
                 (fn [scene]
                   (update scene :layers
                           (fn [layers]
                             (mapv
                              (fn [layer]
                                (update layer :breakpoint dissoc breakpoint))
                              layers))))
                 scenes)))))

(defn disable-message
  [{:keys [breakpoints
           breakpoint]}]
  (let [{:keys [fixed
                orientation]
         :or   {orientation :any}} breakpoint
        {:keys [aspect-ratio
                width
                height]}           fixed
        [width height]             (if aspect-ratio
                                     aspect-ratio
                                     [(:value width) (:value height)])]
    (cond
      (not (seq breakpoint))
      (tr [:no-configuration-added "No configuration added"])
      (contains? (set breakpoints) breakpoint)
      (tr [:this-breakpoint-is-already-added "This breakpoint is already added"])
      (and fixed
           (= orientation :any)
           (not (and width height)))
      (tr [:width-and-height-required "Width and height required"]))))

(defn source-breakpoints
  "Return all breakpoints that define the given value at the given path"
  [layer breakpoints path value {:keys [transform-fn]
                                 :or   {transform-fn identity}}]
  (let [resolve-path (fn [breakpoint]
                       (if (vector? path)
                         (into [:breakpoint breakpoint] path)
                         [:breakpoint breakpoint path]))]
    (->> breakpoints
         (filterv (fn [breakpoint]
                    (= (transform-fn (get-in layer (resolve-path breakpoint))) value))))))

(defn source-breakpoint
  "Return the first breakpoint that defines the given value at the given path"
  [layer breakpoints path value opts]
  (first (source-breakpoints layer breakpoints path value opts)))

(defn breakpoints-with-prop
  "Return all breakpoints that have any value defined at the given path"
  [layer breakpoints path]
  (let [resolve-path (fn [breakpoint]
                       (if (vector? path)
                         (into [:breakpoint breakpoint] path)
                         [:breakpoint breakpoint path]))]
    (->> breakpoints
         (filterv (fn [breakpoint]
                    (some? (get-in layer (resolve-path breakpoint))))))))